import { Kap_Journal, Kap_Program, Mutation_Root } from 'src/@types/graphql'
import {
  CANTONS,
  KAP_JOURNAL_STATUS,
  KAP_JOURNAL_STATUS_TYPE,
  MODULE_TYPES,
  TEXT_LENGTH,
} from 'src/shared/constants/constants'
import { DateUtils } from 'src/shared/utils/DateUtils'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { createDecorators } from 'src/shared/form/utils/decorators'
import { useClient } from 'urql'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useCallback, useEffect, useState } from 'react'
import { HtmlSanitizer } from 'src/shared/utils/HtmlSanitizer'
import { upsertKapJournalMutation } from 'src/screens/kap/program/journal/kapJournalQueries'
import { Form } from 'react-final-form'
import { Stack } from '@mui/material'
import { TextField } from 'src/shared/form/control/TextField'
import { composeValidators, maxChar, required, validDate } from 'src/shared/form/validation/validators'
import { DateTimePickerField } from 'src/shared/form/control/DateTimePickerField'
import { AutoCompleteField } from 'src/shared/form/control/AutoCompleteField'
import { HtmlEditorField } from 'src/shared/form/control/HtmlEditorField'
import { DirtyFormSpy } from 'src/shared/form/dirty/DirtyFormSpy'
import { MultiSelectField } from 'src/shared/form/control/MultiSelectField'
import { Utils } from 'src/shared/utils/Utils'

interface Props {
  kapJournal?: Kap_Journal
  kapProgram: Kap_Program
  modulesInCanton: Set<MODULE_TYPES>
  afterSubmit: (kapJournalId?: number) => void
}

interface FormValues {
  title: string
  modules: MODULE_TYPES[]
  journal_date: string
  status: KAP_JOURNAL_STATUS_TYPE
  correspondence_between?: string | null
  description?: string | null
}

const DEFAULT_FORM_VALUES: FormValues = {
  title: '',
  modules: [],
  journal_date: DateUtils.formatDate(new Date()),
  status: KAP_JOURNAL_STATUS.OPEN,
  correspondence_between: '',
  description: '',
}

export const KapProgramJournalForm = ({ kapJournal, afterSubmit, kapProgram, modulesInCanton }: Props) => {
  const { getMessage } = useMessageSource()

  const decorators = createDecorators()
  const urqlClient = useClient()
  const notificationService = useNotificationService()

  const [initialValues, setInitialValues] = useState<FormValues>(DEFAULT_FORM_VALUES)

  const statusOptions = Object.keys(KAP_JOURNAL_STATUS).map((key) => ({
    label: `${getMessage(`label.program.journal.status.${key}`)}`,
    value: key,
  }))

  const modulesOptions = Utils.sortModules(Array.from(modulesInCanton)).map((module: any) => ({
    label: getMessage(`label.module.description.${module}`),
    value: module,
  }))

  const mapKapJournalToFormValues = useCallback(
    (kap_journal: Kap_Journal): FormValues => {
      return {
        title: kap_journal.title,
        modules: kap_journal.modules.filter((module: MODULE_TYPES) => modulesInCanton.has(module)) ?? [],
        journal_date: DateUtils.parseAndFormatDate(kap_journal.journal_date),
        status: kap_journal.status as KAP_JOURNAL_STATUS_TYPE,
        correspondence_between: kap_journal.correspondence_between,
        description: kap_journal.description,
      }
    },
    [modulesInCanton],
  )

  useEffect(() => {
    const initData = () => {
      if (kapJournal) {
        setInitialValues(mapKapJournalToFormValues(kapJournal))
      }
    }

    initData()
  }, [kapJournal, mapKapJournalToFormValues])

  const onSave = async (values: FormValues) => {
    if (!(kapProgram.canton_code in CANTONS)) {
      notificationService.operationFailed()
      return
    }

    const mutationObject = {
      ...values,
      id: kapJournal?.id,
      kap_program_id: kapProgram.id,
      canton_code: kapProgram.canton_code,
      journal_date: DateUtils.parseAndSerializeDate(values.journal_date),
      correspondence_between: values.correspondence_between
        ? HtmlSanitizer.sanitize(values.correspondence_between)
        : null,
      description: values.description ? HtmlSanitizer.sanitize(values.description) : null,
    }

    const { data } = await urqlClient
      .mutation<{ insert_kap_journal_one: Mutation_Root['insert_kap_journal_one'] }>(upsertKapJournalMutation, {
        object: mutationObject,
      })
      .toPromise()

    if (data?.insert_kap_journal_one) {
      setInitialValues(values)
      notificationService.changesSaved()
      afterSubmit(data.insert_kap_journal_one.id)
    } else {
      notificationService.operationFailed()
    }
  }

  return (
    <>
      {(initialValues || !kapJournal) && (
        <Form<FormValues>
          initialValues={initialValues}
          onSubmit={onSave}
          decorators={decorators}
          render={({ handleSubmit }) => {
            return (
              <form id="kap-journal-form" onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <TextField
                    name="title"
                    label={getMessage('label.program.journal.title')}
                    validate={composeValidators(required(), maxChar(TEXT_LENGTH.M))}
                    required
                  />
                  <MultiSelectField
                    name="modules"
                    label={getMessage('label.modules')}
                    options={modulesOptions}
                    required
                    validate={required()}
                  />
                  <DateTimePickerField
                    name="journal_date"
                    label={getMessage('label.program.journal.date')}
                    validate={composeValidators(required(), validDate())}
                    required
                  />
                  <AutoCompleteField
                    name="status"
                    label={getMessage('label.program.journal.status')}
                    options={statusOptions}
                    validate={required()}
                    required
                  />
                  <HtmlEditorField
                    name="correspondence_between"
                    label={getMessage('label.program.journal.correspondence_between')}
                    validate={maxChar(TEXT_LENGTH.XL)}
                    required
                  />
                  <HtmlEditorField
                    name="description"
                    label={getMessage('label.program.journal.description')}
                    validate={maxChar(TEXT_LENGTH.XL)}
                    required
                  />
                  <DirtyFormSpy />
                </Stack>
              </form>
            )
          }}
        />
      )}
    </>
  )
}
